// src/router/index.js
import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import HomeView from "@/views/HomeView.vue";
import LoginView from "@/views/LoginView.vue";
import RegisterView from "@/views/RegisterView.vue";
import ProfileView from "@/views/ProfileView.vue";
import ForgotPasswordView from "@/views/ForgotPasswordView.vue";
import ResetPasswordView from "@/views/ResetPasswordView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import SelectCountryView from "@/views/SelectCountryView.vue";
import OnboardingView from "@/views/OnboardingView.vue";
import ActivateMembershipView from "@/views/ActivateMembershipView.vue";
import MembershipSuccessView from "@/views/MembershipSuccessView.vue";
import PointsHistory from "@/views/dashboard/modules/membership/PointsHistory.vue";

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: { title: "Hike Society | Columbia - Inicio" },
  },
  {
    path: "/events",
    alias: "/eventos",
    name: "events",
    component: () => import("@/views/EventsView.vue"),
    meta: { title: "Eventos Hike Society Columbia" }
  },
  // Ruta para pagina interna de detalle de eventos
  {
    path: "/events/:id/:slug?",
    name: "event-detail",
    component: () => import("@/views/EventDetailView.vue"),
    meta: { title: "Detalle del Evento" }
  },
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: {
      title: "Iniciar sesión",
      isGuest: true,
    },
  },
  {
    path: "/register",
    name: "register",
    component: RegisterView,
    meta: {
      title: "Registro",
      isGuest: true,
    },
  },
  {
    path: "/onboarding",
    name: "onboarding",
    component: OnboardingView,
    meta: {
      title: "Gracias por registrarte",
      isGuest: false,
    },
  },
  {
    path: "/activate-membership",
    name: "activate-membership",
    component: ActivateMembershipView,
    meta: {
      title: "Activar Membresía",
      isGuest: false,
    },
  },
  {
    path: "/membership-success",
    name: "membership-success",
    component: MembershipSuccessView,
    meta: {
      title: "Membresía Activada",
      isGuest: false,
    },
  },
  {
    path: "/select-country",
    name: "select-country",
    component: SelectCountryView,
    meta: {
      title: "Seleccionar País",
      isGuest: true,
    },
  },

 // RUTAS DEL PASSWORD RESET & FORGOT PASSWORD
{
  path: "/forgot-password",
  name: "forgot-password",
  component: ForgotPasswordView,
  meta: {
    title: "Recuperar Contraseña",
    isGuest: true,
  },
},
{
  path: "/reset-password",
  name: "reset-password",
  component: ResetPasswordView,
  meta: {
    title: "Restablecer Contraseña",
    isGuest: true,
  },
},

  {
    path: "/perfil",
    name: "profile",
    component: ProfileView,
    meta: {
      requiresAuth: true,
      title: "Mi Perfil",
    },
  },
  // Ruta para manejo de autenticación OAuth
  {
    path: "/oauth/callback",
    name: "oauthCallback",
    component: () => import("@/views/OAuthCallback.vue"),
    meta: { requiresGuest: true },
  },
  {
    path: "/terminos-y-condiciones",
    name: "terms",
    component: () => import("@/views/TermsView.vue"),
    meta: { title: "Términos y Condiciones" },


  },
  {
    path: "/dashboard",
    component: () => import("@/views/dashboard/DashboardLayout.vue"),
    children: [
      {
        path: "",
        name: "dashboard",
        component: () => import("@/views/dashboard/DashboardHome.vue"),
        meta: { requiresAuth: true, title: "Mi Dashboard" },
      },
      {
        path: "membership/benefits",
        name: "membership-benefits",
        component: () => import("@/views/dashboard/modules/membership/MembershipBenefits.vue"),
        meta: { requiresAuth: true, title: "Mis Beneficios" },
      },
      {
        path: "membership/details",
        name: "membership-details",
        component: () => import("@/views/dashboard/modules/membership/MembershipDetails.vue"),
        meta: { requiresAuth: true, title: "Mi Membresía" },
      },
      {
        path: 'membership/history',
        name: 'MembershipHistory',
        component: PointsHistory,
        meta: { tab: 'historial' }
      },
      {
        path: "profile/overview",
        name: "profile-overview",
        component: () => import("@/views/dashboard/modules/profile/ProfileOverview.vue"),
        meta: { requiresAuth: true, title: "Mi Perfil" },
      },
      {
        path: "events/my-events",
        name: "my-events",
        component: () => import("@/views/dashboard/modules/events/MyEvents.vue"),
        meta: { requiresAuth: true, title: "Mis Eventos" },
      },
    ]
  },
  // Ruta comodín para capturar todas las rutas que comienzan con /activate/
  {
    path: "/activate/:pathMatch(.*)*",
    redirect: { name: "register" }
  },
  // Ruta 404
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: NotFoundView,
    meta: { title: "Página no encontrada" },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth',
      };
    }

    return { top: 0 };
  },
});

// Guardia de navegación
router.beforeEach(async (to, from, next) => {
  // Establecer título de la página
  document.title = to.meta.title || "Columbia Hike Society";

  const authStore = useAuthStore();
  const isAuthenticated = authStore.isAuthenticated;

  // Si la ruta requiere autenticación
  if (to.meta.requiresAuth && !isAuthenticated) {
    // Si no hay token, redirigir a login
    if (!authStore.token) {
      return next({ name: "login", query: { redirect: to.fullPath } });
    }

    // Si hay token pero no hay datos de usuario, intentar obtenerlos
    try {
      await authStore.fetchUserData();
      return next();
    } catch (error) {
      return next({ name: "login", query: { redirect: to.fullPath } });
    }
  }

  // Si la ruta es solo para invitados y el usuario está autenticado
  if (to.meta.isGuest && isAuthenticated) {
    return next({ name: "home" });
  }

  next();
});

export default router;